import React from 'react';

import {
  RedeeemPackageSubItemPresentational,
} from '../../components/RedeemBasketItem/RedeemBasketItem';
import {
  BlankGiftCardPackProductVariationPresentational,
} from '../../components/ReorderProductsBlock/ReorderProductsBlock';
import {
  Currency,
  GiftcardPackage,
  GiftCardVariationType,
  PersonalMessageProductVariation,
  ProductType,
  WrapperProductVariation,
} from '../../types/apiTypes';
import {
  GiftcardProductVariationPresentational,
  PackageItemsListWrapper,
  RedeemSelectedProduct,
} from '../../types/common';
import {
  DataLayerCheckoutProduct,
  DataLayerProductCategory,
} from '../../types/dataLayer';
import { UsePlaceOrderV1Shipments } from '../checkoutHooks/checkoutHooks';
import { AddToCartParams } from '../dataLayerHooks/dataLayerHooks';
import {
  presentationalMappingHooks,
} from '../presentationalMappingHooks/presentationalMappingHooks';
import { redeemHooks } from '../redeemHooks/redeemHooks';

export const dataLayerMappingHooks = {
  useMapRedeemProductToDataLayerAddToCartProduct() {
    return React.useCallback(
      ({
        selectedProduct,
        personalNoteProduct,
        wrapper,
        productPrice,
        category,
      }: {
        category: DataLayerProductCategory;
        personalNoteProduct: PersonalMessageProductVariation | null;
        productPrice?: string | null;
        selectedProduct: RedeemSelectedProduct | null;
        wrapper: WrapperProductVariation | null;
      }) => {
        const products: DataLayerCheckoutProduct[] = [];

        if (selectedProduct) {
          if (selectedProduct.typename === 'GiftcardProduct') {
            products.push({
              id: selectedProduct.id,
              name: selectedProduct.title,
              category,
              ...(productPrice ? { price: productPrice } : null),
              currency: selectedProduct.redeemData?.
                minimumRemainingBalance.currency || Currency.EUR,
            });
          }
          if (selectedProduct.typename === 'DonationProduct') {
            products.push({
              id: selectedProduct.id,
              name: selectedProduct.redeemData?.title || '',
              category: DataLayerProductCategory.DIGITAL,
              ...(productPrice ? { price: productPrice } : null),
              currency: selectedProduct.redeemData?.
                minimumRemainingBalance?.currency || Currency.EUR,
            });
          }
          if (wrapper) {
            products.push({
              id: wrapper.id,
              name: wrapper.title,
              category: DataLayerProductCategory.ADDITIONAL,
              price: wrapper.price.value.toString(),
              currency: wrapper.price.currency,
            });
          }
        }
        if (personalNoteProduct) {
          products.push({
            id: personalNoteProduct.id,
            name: personalNoteProduct.title,
            category: DataLayerProductCategory.ADDITIONAL,
            price: personalNoteProduct.price.value.toString(),
            currency: personalNoteProduct.price.currency,
          });
        }

        return products;
      },
      [],
    );
  },

  useMapItemsToDataLayerCheckoutProduct() {
    return React.useCallback(
      (
        items?: RedeeemPackageSubItemPresentational[]
          | null,
      ) => {
        const products: DataLayerCheckoutProduct[] = [];
        items?.map((item) => {
          let category: DataLayerProductCategory | null = null;

          if (item.typename === 'GiftcardOrderItem') {
            if (item.variationType === GiftCardVariationType.DIGITAL) {
              category = DataLayerProductCategory.DIGITAL;
            } else {
              category = DataLayerProductCategory.PHYSICAL;
            }
          }

          if (item.typename === 'DonationOrderItem') {
            category = DataLayerProductCategory.DIGITAL;
          }

          if (item.typename === 'WrapperOrderItem') {
            category = DataLayerProductCategory.ADDITIONAL;
          }
          if (item.typename === 'PersonalMessageOrderItem') {
            category = DataLayerProductCategory.ADDITIONAL;
          }

          if (item && category) {
            products.push({
              id: item.variation.id,
              name: item.variation.title,
              quantity: item.qty,
              price: item.variation.price.value.toString(),
              category,
            });
          }
        });
        return products;
      }, [],
    );
  },

  useMapGiftcardPackageToDataLayerCheckoutProduct() {
    return React.useCallback((packages: GiftcardPackage[]) => {
      const products: DataLayerCheckoutProduct[] = [];
      packages.forEach((product) => {
        const giftCard = product.giftcard_v1;
        const category = giftCard?.__typename === 'GiftcardOrderItem'
        && giftCard?.variation.variationType === GiftCardVariationType.DIGITAL
          ? DataLayerProductCategory.DIGITAL
          : DataLayerProductCategory.PHYSICAL;
        products.push({
          id: giftCard?.variation.product.id ?? '',
          name: giftCard?.variation.product.title ?? '',
          quantity: giftCard?.qty,
          price: giftCard?.variation.price.value.toString(),
          category,
        });
        if (product.wrapper) {
          products.push({
            id: product.wrapper.variation.id,
            name: product.wrapper.variation.title,
            quantity: product.wrapper.qty,
            price: product.wrapper.variation.price.value.toString(),
            category: DataLayerProductCategory.ADDITIONAL,
          });
        }
        if (product.message) {
          products.push({
            id: product.message.variation.id,
            name: product.message.variation.title,
            quantity: product.message.qty,
            price: product.message.variation.price.value.toString(),
            category: DataLayerProductCategory.ADDITIONAL,
          });
        }
      });
      return products;
    }, []);
  },

  useMapOrderToDataLayerCheckoutProduct() {
    const getMappedProducts =
    dataLayerMappingHooks.useMapGiftcardPackageToDataLayerCheckoutProduct();
    const packagesToArray = redeemHooks.usePackagesToOneArray();
    return React.useCallback(
      (
        shipments?: UsePlaceOrderV1Shipments,
      ) => {
        const packages = packagesToArray(shipments);
        return getMappedProducts(packages as GiftcardPackage[]);
      },
      [getMappedProducts, packagesToArray],
    );
  },

  useMapRedeemOrderToDataLayerCheckoutProduct() {
    const getMappedProducts = dataLayerMappingHooks.useMapItemsToDataLayerCheckoutProduct();
    const packagesToArray = redeemHooks.usePackagesToOneArray();
    const mapItems = presentationalMappingHooks.useMapRedeemPackageSubItems();
    return React.useCallback(
      (
        shipments?: UsePlaceOrderV1Shipments,
      ): DataLayerCheckoutProduct[] => {
        const packages = packagesToArray(shipments);
        let items: RedeeemPackageSubItemPresentational[] | null = null;
        if (packages) {
          items = packages.
            reduce((packageItems: RedeeemPackageSubItemPresentational[], redeemPackage) => {
              if (redeemPackage.__typename === 'RedeemPackage' && redeemPackage.items) {
                const mappedRedeemPackageItems = mapItems(redeemPackage.items);
                return [...packageItems, ...mappedRedeemPackageItems];
              }
              return packageItems;
            }, []);
        }
        return getMappedProducts(items);
      },
      [getMappedProducts, packagesToArray, mapItems],
    );
  },

  useMapToDataLayerParamsAddToCart(
    variation:
      | GiftcardProductVariationPresentational
      | BlankGiftCardPackProductVariationPresentational
      | null,
    wrapper: Pick<PackageItemsListWrapper, 'variations'> | null,
    quantity: number,
    personalNote: PersonalMessageProductVariation | null,
  ): AddToCartParams | null {
    return React.useMemo(() => {
      if (!variation || !variation.id || !variation.title || !variation.price) {
        return null;
      }
      const products: DataLayerCheckoutProduct[] = [];
      if ('variationType' in variation) {
        const category =
          variation.variationType === GiftCardVariationType.DIGITAL
            ? DataLayerProductCategory.DIGITAL
            : DataLayerProductCategory.PHYSICAL;
        products.push({
          id: variation.id,
          name: variation.title || '',
          price: variation?.price?.value.toString(),
          quantity,
          category,
        });

        if (variation.variationType === GiftCardVariationType.PHYSICAL && wrapper) {
          products.push({
            id: wrapper.variations[0].id,
            name: wrapper.variations[0].title,
            price: wrapper.variations[0].price.value.toString(),
            quantity,
            category: DataLayerProductCategory.ADDITIONAL,
          });
        }

        if (variation.variationType === GiftCardVariationType.PHYSICAL && personalNote) {
          products.push({
            id: personalNote.id,
            name: personalNote.title,
            price: personalNote.price.value.toString(),
            quantity,
            category: DataLayerProductCategory.ADDITIONAL,
          });
        }
      }
      if (
        (variation as BlankGiftCardPackProductVariationPresentational).product
        && (variation as BlankGiftCardPackProductVariationPresentational).product.productType
          === ProductType.BLANK_GIFT_CARD_PACK
      ) {
        products.push({
          id: variation.id,
          name: variation.title || '',
          price: variation?.price?.value.toString(),
          quantity,
          category: DataLayerProductCategory.PHYSICAL,
        });
      }

      return {
        products,
        currencyCode: variation?.price?.currency || Currency.EUR,
      };
    }, [variation, quantity, wrapper, personalNote]);
  },
};
